import {useCurrency} from "hooks";
import { useOrientation } from "hooks/orientation";
import { FC, HTMLAttributes, useState } from "react";
import styled from "styled-components";
import { useWindowSize } from "usehooks-ts";
import { useStore } from "utils/store";
import { Overlay as Dark, MOBILE_WIDTH, Header as HeaderStyle } from 'utils/constants';
import { useTranslation } from "react-i18next";

const Overlay = styled(Dark)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  max-height: 95dvh;
  overflow: hidden;
  width: 400px;
  border-radius: 13px 0 13px 13px;
  border: solid 1px #a9875c;
  box-sizing: border-box;
  background-color: #1f202a;
  position: relative;
  z-index: 20;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;

  @media (orientation: portrait), (max-width: ${MOBILE_WIDTH}) {
    width:90.6vw;
    border-radius: 19px 0 19px 19px;
  }
`;

const Wrapper = styled.div`
  max-height: 85dvh;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }

  &:hover {
    scrollbar-color: #8b8992 transparent;

    &::-webkit-scrollbar-thumb {
      background-color: #8b8992;
    }
  }
`;

const Inner = styled.div`
  box-sizing: border-box;
  padding: 30px 18px;
  height: 100%;
`;


const Image = styled.img`
  object-fit: contain;
  margin: 0 auto;
`;

const Close = styled.div`
  width: 34px;
  height: 34px;
  position: absolute;
  top: 0;
  right: 0;
  background: url(images/close.webp) center / contain no-repeat;
  cursor: pointer;

  @media (orientation: portrait), (max-width: ${MOBILE_WIDTH}) {
    width: 7.6vw;
    height: 7.6vw;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: #fff;
  text-align: center;
  font-family: Teko;
  line-height: 0.83;
  font-size: 15.1px;
  margin-top: 12px;

  & th,
  td {
    border: 1px solid #a9875c;
    height: 27px;

    &.arrow {
      height: 20px;
      cursor: pointer;
      line-height: 1;

      & ${Image} {
        width: 16px;
        vertical-align: middle;
      }
    }

    &.arrow--reverse {
      transform: rotateX(180deg);
    }

    &.bounty {
      width: 75px;
    }
  }

  .hidden {
    display: none;
  }

  & th {
    font-family: Teko;
    background-color: #353645;
    font-size: 15.1px;
    font-weight: normal;

    &.small {
      font-size: 13px;
      line-height: 20px;
    }
  }

  @media (orientation: portrait), (max-width: ${MOBILE_WIDTH}) {
    line-height: 1;
    font-size: clamp(0.75rem, 0.107rem + 2.857vmin, 1.25rem);
    margin-top: 1.4vh;

    & td {
      padding: 0.7vmin 0;

      &.arrow {
        padding: 0.4vmin 0;
      }
    }

    & th {
      padding: 0.5vmin 0;
      font-size: clamp(0.75rem, -0.054rem + 3.571vmin, 1.375rem);

      &.small {
        font-size: clamp(0.625rem, -0.018rem + 2.857vmin, 1.125rem);
      }
    }
  }
`;

const ReturnTable = styled(Table)`
  @media (orientation: portrait), (max-width: ${MOBILE_WIDTH}) {
    font-size: clamp(0.75rem, -0.054rem + 3.571vmin, 1.375rem);
  }

  td {
    width: 50%;
  }
`;

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

const Bounties = styled(Flex)`
  grid-column: 1 / 3;
  grid-row: 1 / 2;
`;

const Return = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 31px;

  @media (orientation: portrait) {
    margin-top: 3.6vh;
  }
`;

const Header = styled(HeaderStyle)`
  font-size: 24px;
  background-position: left 2px, left top;

  @media (orientation: portrait) {
    font-size: clamp(1.75rem, 0.747rem + 3.415vh, 2.625rem);
    background-position: left 3px, left top;
  }
`;

export type PayoutsProps = HTMLAttributes<HTMLDivElement> & {
  show: boolean;
  onClose: () => void;
};

export const Payouts: FC<PayoutsProps> = ({ show, onClose }) => {
  const payouts = useStore(state => state.payouts);
  const [showAll, setShowAll] = useState(false);
  const { parseCurrency } = useCurrency();
  const { portrait } = useOrientation();
  const { height } = useWindowSize();
  const { t } = useTranslation();

  function getBuyIns() {
    return payouts.filter((p) => p.distributionMatrix.length).map(p => p.buyIn);
  }

  function getBounty(buyIn: string, key: { numBounty: number, index: number }) {
    const payout = payouts.find((p) => p.buyIn === buyIn);
    let bounty = '';

    if (payout) {
      const matrices = payout.distributionMatrix.filter(m => m[1] === key.numBounty);

      if (matrices[key.index]) {
        bounty = matrices[key.index][0];
      }
    }

    return bounty;
  }

  function getMatrixKeys() {
    const maxCountMap = new Map<number, number>();

    payouts.forEach(payout => {
      const countMap = new Map<number, number>();

      for (const matrix of payout.distributionMatrix) {
        countMap.set(matrix[1], (countMap.get(matrix[1]) || 0) + 1);
      }

      for (const [num, count] of Array.from(countMap.entries())) {
        const currentMax = maxCountMap.get(num) || 0;
        if (count > currentMax) {
          maxCountMap.set(num, count);
        }
      }
    });

    const result: { numBounty: number, index: number }[] = [];

    Array.from(maxCountMap.entries())
      .sort((a, b) => a[0] - b[0]) 
      .forEach(([numBounty, max]) => {
        for (let i = 0; i < max; i++) {
          result.push({ numBounty, index: i });
        }
      });

    return result;
  }

  function getRTP() {
    const payout = payouts.find((p) => p.buyIn);

    if (!payout) {
      return {};
    }

    return {
      bounties: +payout?.bountyRtp,
      jackpots: +payout?.jackpotRtp,
      total: +payout?.bountyRtp + +payout?.jackpotRtp
    }
  }

  if (!show) return null;

  return (
    <Overlay>
      <Container data-testid="pay-table">
        <Wrapper>
          <Inner>
            <Bounties>
              <Header>{t('BUY-INS')}</Header>
              <Table>
                <tbody>
                  <tr>
                    <th className="small bounty">{t('# of Bounties')}</th>
                    {getBuyIns().map((buyIn) => (
                      <th key={buyIn}>{parseCurrency(buyIn)}</th>
                    ))}
                  </tr>
                  {getMatrixKeys().map((key, index) => (
                    <tr key={`${key.numBounty}-${key.index}`} className={!showAll && index >= 4 ? 'hidden' : ''}>
                      <td>{key.numBounty}</td>
                      {getBuyIns().map((buyIn) => (
                        <td key={buyIn}>{getBounty(buyIn, key) ? parseCurrency(getBounty(buyIn, key)) : ""}</td>
                      ))}
                    </tr>
                  ))}
                  {getMatrixKeys().length >= 4 &&
                    <tr>
                      <td className={showAll ? 'arrow--reverse arrow' : 'arrow'} colSpan={payouts.length + 1} onClick={() => setShowAll(!showAll)}>
                        <Image src="images/arrow-down.webp" height={portrait ? height * 0.02 : 18} draggable={false} />
                      </td>
                    </tr>
                  }
                </tbody>
              </Table>
            </Bounties>
            <Return>
            <Header>{t('RETURN TO PLAYER')}</Header>
              <ReturnTable>
                <tbody>
                  <tr>
                    <th>{t('Bounties')}</th>
                    <td>{getRTP().bounties}%</td>
                  </tr>
                  <tr>
                    <th>{t('Jackpots')}</th>
                    <td>{getRTP().jackpots}%</td>
                  </tr>
                  <tr>
                    <th>{t('TOTAL')}</th>
                    <td>{getRTP().total}%</td>
                  </tr>
                </tbody>
              </ReturnTable>
            </Return>
          </Inner>
        </Wrapper>
        <Close onClick={onClose} />
      </Container>
    </Overlay>
  );
};

